import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { LabelPrintService } from '../../../core/services/label-print.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { forkJoin, firstValueFrom, throwError, timer, TimeoutError } from 'rxjs';
import { timeout, retry } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { BOOK_TABLE_BASE } from '../table-configs';
import { BookFormComponent, BookSavedEvent } from '../book-form/book-form.component';
import { AppDatePipe } from '../../../core/pipes/app-date.pipe';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-book-master',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module, BookFormComponent, AppDatePipe],
  templateUrl: './book-master.component.html',
  styleUrl: './book-master.component.scss',
})
export class BookMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';

  // List state
  records: any[] = [];
  isListLoading = false;
  loadProgress = { loaded: 0, total: 0 };
  loadError = '';
  loadSlow = false;
  private loadSlowTimer: ReturnType<typeof setTimeout> | null = null;
  // The server now caches the full sorted book list in memory (see
  // bookMaster.services.js#_getCachedBooks) and serves search/pagination from it, so a
  // single request is enough — no more chunked client-side fetching against a live Mongo scan.
  private readonly LOAD_TIMEOUT_MS = 20000;
  private readonly SLOW_LOAD_HINT_MS = 6000;

  // Form state
  isEditMode = false;
  editId: string | null = null;
  isFormLoading = false;
  editLoadError = '';
  pendingEditBook: any | null = null;

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  // History panel
  showHistory = false;
  historyTitle = '';
  historyLoading = false;
  historyData: any[] = [];

  // Add Copy modal
  showAddCopyModal = false;
  addCopyBook: any = null;
  private returnToListAfterModal = false;
  addCopyStep: 'form' | 'result' = 'form';
  addCopyCopies = 1;
  addCopyCenterId = '';
  addCopyLocation = '';
  addCopyIsCreating = false;
  addCopyError = '';
  addCopyGeneratedSrNos: number[] = [];
  addCopySelectedCenter: any = null;

  centers: any[] = [];
  centersLoading = false;

  // Author books panel (hyperlink drill-down)
  showAuthorPanel = false;
  authorPanelTitle = '';
  authorPanelBooks: any[] = [];
  isAuthorPanelLoading = false;
  authorPanelError = '';

  // Copies modal (within author panel)
  showCopiesModal = false;
  copiesModalBook: any = null;
  copiesModalLoading = false;
  copiesModalError = '';

  tableConfig: VT2_TABLE_CONFIG = {
    ...BOOK_TABLE_BASE,
    maxActionButtons: 3,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
      { icon: '📋', title: 'History', cssClass: '', onClick: (row) => this.openHistory(row) },
      { icon: '➕', title: 'Add Copy', cssClass: '', onClick: (row) => this.openAddCopyModal(row) },
    ],
    hyperlinkFields: {
      authorNames: {
        onClick: (value: any) => {
          const names: string[] = Array.isArray(value) ? value.filter(Boolean) : [String(value)];
          if (names.length) this.openAuthorPanel(names);
        },
      },
      categoryNames: {
        onClick: (value: any) => {
          const names: string[] = Array.isArray(value) ? value.filter(Boolean) : [String(value)];
          if (names.length) this.openCategoryPanel(names);
        },
      },
    },
  };

  get isManagerRole(): boolean { return this.auth.getCurrentUser()?.role === 'manager'; }

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private centerService: CenterService,
    private labelPrint: LabelPrintService,
    private pageTitle: PageTitleService,
    private router: Router,
    private dateFmt: DateFormatService,
  ) { }

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Book Master', 'Manage library book catalog');
    this.loadList();
  }

  loadList(): void {
    this.isListLoading = true;
    this.loadError = '';
    this.loadSlow = false;
    this.loadProgress = { loaded: 0, total: 0 };
    this._loadBooks();
  }

  // Only retry transient failures (timeouts, dropped connections, 5xx). App-level failures —
  // e.g. an expired session, which api.service already turns into auth.logout() — are plain
  // Errors and should surface immediately rather than being retried three times over.
  private _retryTransient() {
    return retry<any>({
      count: 2,
      delay: (err: any) => {
        const retryable = err instanceof TimeoutError || err instanceof HttpErrorResponse;
        return retryable ? timer(400) : throwError(() => err);
      },
    });
  }

  private async _loadBooks(): Promise<void> {
    this.loadSlowTimer = setTimeout(() => { this.loadSlow = true; }, this.SLOW_LOAD_HINT_MS);
    try {
      const books = await firstValueFrom(
        this.api.getBooks({}).pipe(timeout(this.LOAD_TIMEOUT_MS), this._retryTransient())
      );
      this.records = books || [];
    } catch (err: any) {
      this.records = [];
      this.loadError = err?.message || 'Failed to load books — please check your connection and try again.';
    } finally {
      if (this.loadSlowTimer) clearTimeout(this.loadSlowTimer);
      this.loadSlow = false;
      this.isListLoading = false;
    }
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.pendingEditBook = null;
    this.editLoadError = '';
    this.view = 'form';
    this.pageTitle.set('Book Master', 'Add a new book to the library catalog');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.pendingEditBook = null;
    this.editLoadError = '';
    this.isFormLoading = true;
    this.view = 'form';
    this.pageTitle.set('Book Master', 'Update existing book details');

    this.api.getBookById(this.editId!).subscribe({
      next: (book) => {
        this.pendingEditBook = book;
        this.isFormLoading = false;
      },
      error: () => {
        this.editLoadError = 'Failed to load book details.';
        this.isFormLoading = false;
      },
    });
  }

  onBookSaved(event: BookSavedEvent): void {
    if (this.isEditMode) {
      // Refresh just the edited row (name/author/category/active may have changed) instead of
      // reloading all ~30k books.
      if (this.editId) this._upsertBookRow(this.editId);
      setTimeout(() => this.backToList(), 1000);
    } else {
      this.returnToListAfterModal = true;
      this._upsertBookRow(event.result.id); // show it in the list even if the copy modal is cancelled
      this.openAddCopyModal({
        _id:      event.result.id,
        bookId:   event.result.bookId,
        bookName: event.bookName,
      });
    }
  }

  backToList(): void {
    this.view = 'list';
    this.pendingEditBook = null;
    this.pageTitle.set('Book Master', 'Manage library book catalog');
  }

  refreshList(): void { this.loadList(); }

  openCatalogue(): void {
    this.router.navigate(['/book-catalogue'], { state: { returnTo: '/books' } });
  }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteBook(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete book.';
      },
    });
  }

  openHistory(record: any): void {
    this.historyTitle = record.bookName;
    this.historyData = [];
    this.showHistory = true;
    this.historyLoading = true;
    this.api.getBookHistory(record.bookId).subscribe({
      next: (data) => { this.historyData = data || []; this.historyLoading = false; },
      error: () => { this.historyLoading = false; },
    });
  }

  closeHistory(): void { this.showHistory = false; }

  // ── Add Copy modal ─────────────────────────────────────────────────────────

  openAddCopyModal(book: any): void {
    this.addCopyBook = book;
    this.addCopyStep = 'form';
    this.addCopyCopies = 1;
    this.addCopyCenterId = '';
    this.addCopyLocation = '';
    this.addCopyError = '';
    this.addCopyGeneratedSrNos = [];
    this.addCopySelectedCenter = null;
    this.showAddCopyModal = true;

    if (this.isManagerRole) {
      const mc = this.centerService.selectedCenter ?? this.centerService.getCachedCenters()[0] ?? null;
      this.addCopyCenterId       = mc?._id || '';
      this.addCopySelectedCenter = mc;
    } else {
      if (!this.centers.length && !this.centersLoading) {
        this.centersLoading = true;
        this.api.getCenters().subscribe({
          next: (data) => {
            this.centers = data || [];
            if (this.centers.length) this.addCopyCenterId = this.centers[0]._id;
            this.centersLoading = false;
          },
          error: () => { this.centersLoading = false; },
        });
      } else if (this.centers.length) {
        this.addCopyCenterId = this.centers[0]._id;
      }
    }
  }

  closeAddCopyModal(): void {
    this.showAddCopyModal = false;
    this.addCopyBook = null;
    if (this.returnToListAfterModal) {
      this.returnToListAfterModal = false;
      this.backToList();
    }
  }

  createCopies(): void {
    const copies = Number(this.addCopyCopies);
    if (!copies || copies < 1 || copies > 200) { this.addCopyError = 'Enter between 1 and 200 copies.'; return; }
    if (!this.addCopyCenterId) { this.addCopyError = 'Please select a center.'; return; }
    this.addCopyIsCreating = true;
    this.addCopyError = '';
    this.addCopySelectedCenter = this.centers.find(c => c._id === this.addCopyCenterId) ?? this.addCopySelectedCenter ?? null;
    this.api.addCopies({ id: this.addCopyBook._id, numberOfCopies: copies, centerId: this.addCopyCenterId, location: this.addCopyLocation.trim() }).subscribe({
      next: (result: any) => {
        this.addCopyGeneratedSrNos = (result.generatedSrNos || []).map((n: any) => Number(n));
        this.addCopyStep = 'result';
        this.addCopyIsCreating = false;
        // Patch just this row rather than reloading all ~30k books (that reload tore down the
        // table and left "Loading books…" behind the modal). A brand-new book isn't in the list
        // yet, so fetch and insert it.
        const bookId = this.addCopyBook._id;
        const row = this.records.find(r => r._id === bookId);
        if (row) {
          Object.assign(row, { totalCopies: result.newTotalCopies, copiesAvailable: result.newCopiesAvailable });
          this.records = [...this.records]; // same row objects, new reference → table refreshes, keeps page/search
        } else {
          this._upsertBookRow(bookId);
        }
      },
      error: (err: any) => {
        this.addCopyError = err?.message || 'Failed to create copies.';
        this.addCopyIsCreating = false;
      },
    });
  }

  // Replaces (or inserts) one book's row in its sorted position. A book that is now
  // inactive is dropped, since the list only shows active books.
  private _upsertBookRow(id: string): void {
    this.api.getBookById(id).subscribe({
      next: (book) => {
        if (!book) return;
        const rest = this.records.filter(r => r._id !== book._id);
        if (book.isActive === false) { this.records = rest; return; }
        const name = book.bookName || '';
        const idx = rest.findIndex(r => (r.bookName || '').localeCompare(name) > 0); // list is sorted by bookName
        rest.splice(idx < 0 ? rest.length : idx, 0, book);
        this.records = rest;
      },
      error: () => this.loadList(), // fall back to a full reload
    });
  }

  printAndClose(): void {
    if (this.addCopyGeneratedSrNos.length) {
      this._doPrint(this.addCopyBook, this.addCopyGeneratedSrNos);
    }
    this.closeAddCopyModal();
  }

  padSrNo(n: number): string { return String(n).padStart(6, '0'); }

  private async _doPrint(book: any, srNos: number[]): Promise<void> {
    if (!srNos.length) return;
    const clientName = await this.labelPrint.resolveClientName();
    this.labelPrint.print(book?.bookName || '', srNos, clientName);
  }

  // ── Books Panel (author / category drill-down) ─────────────────────────────

  openAuthorPanel(names: string[]): void {
    this._openBooksPanel(`Books by ${names.join(', ')}`, names.map(n => this.api.locateBooksByAuthor(n)));
  }

  openCategoryPanel(names: string[]): void {
    this._openBooksPanel(`Books in ${names.join(', ')}`, names.map(n => this.api.getBooksByCategory(n)));
  }

  private _openBooksPanel(title: string, requests: any[]): void {
    this.authorPanelTitle = title;
    this.showAuthorPanel = true;
    this.authorPanelBooks = [];
    this.isAuthorPanelLoading = true;
    this.authorPanelError = '';
    this.showCopiesModal = false;

    forkJoin(requests).subscribe({
      next: (results) => {
        const seen = new Set<string>();
        const merged: any[] = [];
        (results as any[][]).flat().forEach((book: any) => {
          const key = book.bookId || book.bookName;
          if (!seen.has(key)) { seen.add(key); merged.push(book); }
        });
        this.authorPanelBooks = merged;
        this.isAuthorPanelLoading = false;
      },
      error: (err: any) => {
        this.authorPanelError = err?.message || 'Failed to load books.';
        this.isAuthorPanelLoading = false;
      },
    });
  }

  closeAuthorPanel(): void {
    this.showAuthorPanel = false;
    this.showCopiesModal = false;
  }

  openBookCopies(book: any): void {
    this.copiesModalError = '';
    this.showCopiesModal = true;

    if (Array.isArray(book.copies)) {
      this.copiesModalBook = book;
      this.copiesModalLoading = false;
      return;
    }

    this.copiesModalBook = { ...book, copies: null };
    this.copiesModalLoading = true;
    this.api.getBookCopies(book._id).subscribe({
      next: (copies) => { this.copiesModalBook.copies = copies || []; this.copiesModalLoading = false; },
      error: (err: any) => { this.copiesModalError = err?.message || 'Failed to load copies.'; this.copiesModalLoading = false; },
    });
  }

  closeCopiesModal(): void {
    this.showCopiesModal = false;
    this.copiesModalBook = null;
    this.copiesModalLoading = false;
    this.copiesModalError = '';
  }

  copyStatusLabel(status: string): string {
    if (status === 'C') return 'At Center';
    if (status === 'M') return 'Issued';
    if (status === 'S') return 'Scrapped';
    return status;
  }

  copyRowClass(status: string): string {
    if (status === 'C') return 'bm-hl-row-c';
    if (status === 'M') return 'bm-hl-row-m';
    if (status === 'S') return 'bm-hl-row-s';
    return '';
  }
}
